import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowBack, Book, Refresh as RefreshIcon, Add as AddIcon, ThumbUp as ThumbUpIcon, ThumbUpOutlined as ThumbUpOutlinedIcon, KeyboardArrowUp as KeyboardArrowUpIcon } from '@mui/icons-material';

import { useAuth } from '../hooks/useAuth';
import { useTheme } from '../utils/theme-context';
import apiService from '../services/api';
import { LoadingOverlaySkeleton } from '../components/SkeletonLoader';
import { StudyContentSkeleton } from '../components/SkeletonLoader';
import TTSPlayer from '../components/TTSPlayer';

const StudyContent = () => {
  const { subject, chapter } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const { themeTexts, themeIcons } = useTheme();
  const [content, setContent] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [existingContent, setExistingContent] = useState([]);
  const [showExistingContent, setShowExistingContent] = useState(false);
  const [generatingNew, setGeneratingNew] = useState(false);
  const [isRelearn, setIsRelearn] = useState(false);
  const [originalContent, setOriginalContent] = useState(null);
  const [hasInitialized, setHasInitialized] = useState(false);
  const [ongoingRequest, setOngoingRequest] = useState(false);
  const regenerateRequestRef = useRef(false);
  const [contentId, setContentId] = useState(null);
  const [isLiked, setIsLiked] = useState(false);
  const [updatingLike, setUpdatingLike] = useState(false);
  const [showScrollTop, setShowScrollTop] = useState(false);

  // Reset state when subject or chapter changes
  useEffect(() => {
    // Debug logging removed for performance
    setHasInitialized(false);
    setContent('');
    setExistingContent([]);
    setShowExistingContent(false);
    setError(null);
    setOngoingRequest(false);
    regenerateRequestRef.current = false; // Reset ref
  }, [subject, chapter]);

  // Handle scroll to top button
  useEffect(() => {
    const handleScroll = () => {
      setShowScrollTop(window.scrollY > 300);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToTop = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  useEffect(() => {
    const fetchStudyContent = async () => {
      // Debug logging removed for performance
      
      // Prevent multiple initializations
      if (hasInitialized || ongoingRequest) {
        // Debug logging removed for performance
        return;
      }
      
      try {
        setOngoingRequest(true);
        setLoading(true);
        setError(null);
        
        // Check if we have content from navigation state
        if (location.state && location.state.content) {
          // Debug logging removed for performance
          setContent(extractContentFromResponse(location.state.content));
          setIsRelearn(location.state.isRelearn || false);
          setOriginalContent(location.state.originalContent || null);
          
          // Handle existing content with regenerate option
          if (location.state.isExisting && location.state.contentId) {
            // Debug logging removed for performance
            // Store the content ID for regenerate functionality
            setContentId(location.state.contentId);
          }
          
          setShowExistingContent(false);
          setLoading(false);
          setHasInitialized(true);
          return;
        }
        
        // First, try to get existing content
        const existingResponse = await apiService.getExistingContent('study', subject, chapter);
        
        if (existingResponse.content && existingResponse.content.length > 0) {
          setExistingContent(existingResponse.content);
          setShowExistingContent(true);
          setLoading(false);
          setHasInitialized(true);
          return;
        }
        
        // If no existing content, generate new content
        const response = await apiService.generateStudyContent(subject, chapter);
        // Debug logging removed for performance
        setContent(extractContentFromResponse(response));
        setShowExistingContent(false);
        setHasInitialized(true);
        // Debug logging removed for performance
      } catch (err) {
        console.error('Error fetching study content:', err);
        setError('Failed to load study content. Please try again.');
      } finally {
        setLoading(false);
        setOngoingRequest(false);
      }
    };

    if (subject && chapter) {
      fetchStudyContent();
    }
  }, [subject, chapter, hasInitialized]); // Added hasInitialized to dependencies

  const handleGenerateNew = async () => {
    // Debug logging removed for performance
    
    // Prevent multiple calls using both state and ref
    if (generatingNew || regenerateRequestRef.current) {
      // Debug logging removed for performance
      return;
    }
    
    try {
      regenerateRequestRef.current = true; // Set ref immediately
      setGeneratingNew(true);
      setError(null);
      // Debug logging removed for performance
      
      // If we have a contentId, use relearn to regenerate the specific content
      if (contentId) {
        // Debug logging removed for performance
        const response = await apiService.relearnContent('study', contentId);
        // Debug logging removed for performance
        setContent(extractContentFromResponse(response));
      } else {
        // Generate completely new content
        const response = await apiService.regenerateStudyContent(subject, chapter);
        // Debug logging removed for performance
        setContent(extractContentFromResponse(response));
      }
      
      setShowExistingContent(false);
      // Debug logging removed for performance
      
      // Don't refresh existing content list here to avoid loops
    } catch (err) {
      console.error('Error generating new content:', err);
      setError('Failed to generate new content. Please try again.');
    } finally {
      setGeneratingNew(false);
      regenerateRequestRef.current = false; // Reset ref
      // Debug logging removed for performance
    }
  };

  const handleRelearn = async (contentId) => {
    try {
      setLoading(true);
      setError(null);
      
      const response = await apiService.relearnContent('study', contentId);
      // Debug logging removed for performance
      setContent(extractContentFromResponse(response));
      setShowExistingContent(false);
    } catch (err) {
      console.error('Error relearning content:', err);
      setError('Failed to load content. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    // Get the book parameter from the URL or location state
    const bookParam = location.state?.book || '';
    navigate(`/study/${subject}${bookParam ? `/${bookParam}` : ''}`);
  };

  const handleLike = async () => {
    if (!contentId || updatingLike) return;
    
    setUpdatingLike(true);
    try {
      await apiService.likeContent(contentId, 'study', !isLiked);
      setIsLiked(!isLiked);
    } catch (error) {
      console.error('Error updating like status:', error);
    } finally {
      setUpdatingLike(false);
    }
  };

  // Helper function to extract content from API response
  const extractContentFromResponse = (response) => {
    // If response has the new structured format, return the full response
    if (response.title || (response.summary && response.key_points && response.detailed_content)) {
      return response;
    } else if (response.detailed_content) {
      return response.detailed_content;
    } else if (response.summary) {
      return response.summary;
    } else if (response.content) {
      return response.content;
    } else if (typeof response === 'string') {
      return response;
    } else {
      return 'No content available.';
    }
  };

  const renderStructuredContent = (content) => {
    // Handle null/undefined content
    if (!content) {
      return <p className="text-gray-500 dark:text-gray-400">No content available.</p>;
    }
    
    // If content is a string, use the old markdown renderer
    if (typeof content === 'string') {
      return renderMarkdownContent(content);
    }
    
    // Handle structured content with summary, key_points, detailed_content
    return (
      <div className="space-y-4">
        {/* Summary - Compact */}
        {content.summary && (
          <div className="bg-blue-50 dark:bg-blue-900/20 rounded-lg p-3">
            <h2 className="text-base font-semibold text-blue-800 dark:text-blue-300 mb-2">
              📋 Summary
            </h2>
            <p className="text-blue-700 dark:text-blue-200 text-sm leading-relaxed">
              {content.summary}
            </p>
          </div>
        )}
        
        {/* Key Points - Compact */}
        {content.key_points && content.key_points.length > 0 && (
          <div className="bg-yellow-50 dark:bg-yellow-900/20 rounded-lg p-3">
            <h2 className="text-base font-semibold text-yellow-800 dark:text-yellow-300 mb-2">
              ⭐ Key Points
            </h2>
            <ul className="space-y-1">
              {content.key_points.map((point, index) => (
                <li key={index} className="flex items-start text-sm">
                  <span className="text-yellow-600 dark:text-yellow-400 font-bold mr-2">•</span>
                  <span className="text-yellow-700 dark:text-yellow-200">{point}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        
        {/* Detailed Content - Compact */}
        {content.detailed_content && (
          <div>
            <h2 className="text-lg font-semibold text-gray-800 dark:text-gray-200 mb-3">
              📖 Detailed Content
            </h2>
            <div className="text-gray-700 dark:text-gray-300 leading-relaxed text-sm">
              {renderMarkdownContent(content.detailed_content)}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderMarkdownContent = (text) => {
    // Handle null/undefined text
    if (!text) {
      return <p className="text-gray-500 dark:text-gray-400">No content available.</p>;
    }
    
    // Simple markdown rendering for bold text and bullet points
    return text
      .split('\n')
      .map((line, index) => {
        // Handle bold text
        line = line.replace(/\*\*(.*?)\*\*/g, '<strong class="text-teal-600 dark:text-teal-400 font-semibold">$1</strong>');
        
        // Handle bullet points
        if (line.trim().startsWith('•') || line.trim().startsWith('-')) {
          return (
            <li key={index} className="ml-4 mb-1 text-gray-700 dark:text-gray-300 text-sm" 
                dangerouslySetInnerHTML={{ __html: line.replace(/^[•-]\s*/, '') }} />
          );
        }
        
        // Handle numbered lists
        if (/^\d+\./.test(line.trim())) {
          return (
            <li key={index} className="ml-4 mb-1 text-gray-700 dark:text-gray-300 text-sm" 
                dangerouslySetInnerHTML={{ __html: line.replace(/^\d+\.\s*/, '') }} />
          );
        }
        
        // Handle headers
        if (line.startsWith('#')) {
          const level = line.match(/^#+/)[0].length;
          const text = line.replace(/^#+\s*/, '');
          const Tag = `h${Math.min(level, 6)}`;
          return (
            <Tag key={index} className={`font-bold text-teal-700 dark:text-teal-300 mb-2 ${
              level === 1 ? 'text-lg' : level === 2 ? 'text-base' : 'text-sm'
            }`}>
              {text}
            </Tag>
          );
        }
        
        // Regular paragraph
        if (line.trim()) {
          return (
            <p key={index} className="mb-2 text-gray-700 dark:text-gray-300 leading-relaxed text-sm"
               dangerouslySetInnerHTML={{ __html: line }} />
          );
        }
        
        return <br key={index} />;
      });
  };

  if (loading) {
    return <StudyContentSkeleton />;
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
        <div className="container mx-auto px-4 py-8">
          <div className="max-w-4xl mx-auto">
            <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-6 mb-6">
              <p className="text-red-700 dark:text-red-300">{error}</p>
            </div>
            <button
              onClick={handleBack}
              className="flex items-center gap-2 text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300"
            >
              <ArrowBack className="w-5 h-5" />
              Back to Study
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 dark:from-gray-900 dark:to-gray-800">
      <div className="container mx-auto px-4 py-6">
        <div className="max-w-5xl mx-auto">
          {/* Ultra-Compact Header */}
          <motion.div
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-4"
          >
            <div className="flex items-center justify-between">
              <button
                onClick={handleBack}
                className="flex items-center gap-2 text-teal-600 dark:text-teal-400 hover:text-teal-700 dark:hover:text-teal-300 transition-colors"
              >
                <ArrowBack className="w-5 h-5" />
                Back to Study
              </button>
              
              <div className="flex items-center gap-2">
                {/* Status badges */}
                {isRelearn && (
                  <span className="px-2 py-1 bg-orange-100 dark:bg-orange-900 text-orange-800 dark:text-orange-200 text-xs font-medium rounded-full">
                    Previous Content
                  </span>
                )}
                {location.state && location.state.isNew && (
                  <span className="px-2 py-1 bg-green-100 dark:bg-green-900 text-green-800 dark:text-green-200 text-xs font-medium rounded-full">
                    New Content
                  </span>
                )}
                
                {/* Like Button */}
                {contentId && (
                  <button
                    onClick={handleLike}
                    disabled={updatingLike}
                    className={`flex items-center gap-1 px-2 py-1 rounded-md text-xs font-medium transition-colors ${
                      isLiked 
                        ? 'bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300' 
                        : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-green-50 dark:hover:bg-green-900/30'
                    }`}
                  >
                    {updatingLike ? (
                      <div className="w-3 h-3 border-2 border-current border-t-transparent rounded-full animate-spin" />
                    ) : isLiked ? (
                      <ThumbUpIcon className="w-4 h-4" />
                    ) : (
                      <ThumbUpOutlinedIcon className="w-4 h-4" />
                    )}
                  </button>
                )}
              </div>
            </div>
          </motion.div>

          {/* Page Title */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 }}
            className="mb-4"
          >
            <h1 className="text-2xl font-bold text-gray-800 dark:text-gray-200 text-center">
              Summary Page - {chapter}
            </h1>
          </motion.div>

          {/* Ultra-Compact Content */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white/80 dark:bg-gray-900/80 backdrop-blur-lg rounded-xl shadow-lg p-4"
          >
            {/* TTS Player and Regenerate Button - Side by Side */}
            <div className="flex justify-end items-center gap-3 mb-3">
              {/* TTS Player */}
              {content && <TTSPlayer content={content} />}
              
              {/* Regenerate Button */}
              <button
                onClick={handleGenerateNew}
                disabled={generatingNew}
                className="flex items-center gap-1 bg-teal-600 hover:bg-teal-700 disabled:bg-teal-400 text-white px-3 py-1.5 rounded-md text-sm font-medium transition-colors"
              >
                {generatingNew ? (
                  <>
                    <div className="w-3 h-3 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <RefreshIcon className="w-4 h-4" />
                    Regenerate
                  </>
                )}
              </button>
            </div>
            
            {/* Content */}
            <div className="prose prose-sm max-w-none dark:prose-invert">
              {renderStructuredContent(content)}
            </div>
          </motion.div>
        </div>
      </div>

      {/* Scroll to Top Button */}
      {showScrollTop && (
        <motion.button
          initial={{ opacity: 0, scale: 0 }}
          animate={{ opacity: 1, scale: 1 }}
          exit={{ opacity: 0, scale: 0 }}
          onClick={scrollToTop}
          className="fixed bottom-6 right-6 bg-teal-600 hover:bg-teal-700 text-white p-3 rounded-full shadow-lg transition-all duration-200 hover:scale-110 z-50"
        >
          <KeyboardArrowUpIcon className="w-6 h-6" />
        </motion.button>
      )}
    </div>
  );
};

export default StudyContent; 